// Copy for the Unified Feedback Inbox (`src/scenes/FeedbackInbox`), mounted
// from Reports/OrderFeedbackReport's "Feedback" tab and RecoveryAI/Landing's
// "Feedback" tab. Sourced from
// veda:projects/unified-feedback-inbox/prototype/v69/ANALYSIS.md "Copy
// Inventory" and docs/06-ux-rules.md. Pinned/closed strings are called out
// inline; everything else is drafted to match their style pending design
// sign-off on the individual components (PRs 02-08).
export default {
  filter_bar: {
    status: {
      all: 'All',
      open: 'Open',
      resolved: 'Resolved',
    },
    // aria-label description (no visible tooltip per design) — pin #1153,
    // ✓ closed for "all"/"open"; the remaining segments are drafted in the
    // same voice pending design sign-off — see ANALYSIS.md "Copy Inventory".
    status_tooltip: {
      all: 'All: All order ratings',
      open: 'Open: Unresolved and escalated order ratings',
      resolved: 'Resolved: Order ratings that have been resolved',
    },
    status_group_aria: 'Filter by status',
    // "+1" pulse on the Resolved segment after a resolve completes
    // (docs/06-ux-rules.md "Resolve animation"; decorative, aria-hidden —
    // the real state change is announced separately via
    // `actions.resolved_announcement`).
    resolved_pulse: '+1',
    kind: {
      feedback: 'Order Feedback',
      recovery_ai: 'RecoveryAI Escalations',
      all: 'All',
    },
    kind_tooltip: {
      feedback: 'Order Feedback: Thumbs-down ratings with a written comment',
      recovery_ai:
        'RecoveryAI Escalations: Conversations RecoveryAI escalated or resolved',
      all: 'All: Every order rating, positive and negative',
    },
    kind_group_aria: 'Filter by source',
    // CountBadge's required sr-only label for a kind segment's count pill —
    // never applied to "All" (docs/06-ux-rules.md "Counts"). In practice this
    // text is inert: the segment `Button` sets its own `aria-label` (pin
    // #1153 tooltip copy), which per the ARIA accname algorithm fully
    // replaces the button's accessible name and short-circuits before any
    // subtree content — including this sr-only span — is considered. Kept
    // non-empty (rather than passing srLabel="") so the count is still
    // surfaced if that aria-label pattern ever changes, without risking a
    // double-announcement today. Not a pluralized sentence (no word changes
    // shape with count), so no key/key_plural pair is needed (same call as
    // the old kind_with_count key this replaces).
    kind_count_srlabel: '%{label}: %{count}',
    rating_up_aria: 'Show thumbs-up ratings',
    rating_down_aria: 'Show thumbs-down ratings',
    rating_group_aria: 'Filter by rating',
    location: {
      trigger: 'Location',
      trigger_with_count: 'Location (%{count})',
      search_placeholder: 'Search locations',
      no_matches: 'No locations match your search.',
      all_locations: 'All locations',
      list_aria: 'Locations',
      unnamed_location: 'Unnamed location',
      // RecoveryAI on at some locations but not all (Sketch v83 pilot).
      rai_count: '%{on} of %{total} on',
      rai_count_sr: 'RecoveryAI is on at %{on} of %{total} locations',
      rai_on_quick_select: 'RecoveryAI: On',
      rai_off_quick_select: 'RecoveryAI: Off',
      rai_on_pill: 'RecoveryAI on',
      rai_off_pill: 'Off',
      rai_off_pill_sr: 'RecoveryAI off',
    },
  },
  list: {
    search_placeholder: 'Search for guests and comments',
    search_aria: 'Search feedback',
    clear_search: 'Clear search',
    unknown_guest: 'Guest',
    rows_aria: 'Feedback',
    // Curly quotes around the row preview, matching the prototype's
    // `ibSnippet()` (index.html:2265) — kept in i18n data (not inline JSX
    // glyphs) per the repo convention against raw Unicode/HTML entities in
    // JSX.
    preview_quoted: '“%{comment}”',
    empty: {
      // Scoped by the same filter-combination causes as the empty-state
      // table in docs/06-ux-rules.md "Per-pane states".
      open_feedback_title: 'No open feedback',
      // FR-26: Order Feedback lists commented thumbs-down only, so point at
      // All for the ratings with no comment.
      open_feedback_body:
        'No written feedback needs a response right now. Ratings without a comment are under All.',
      open_recovery_ai_title: 'No open RecoveryAI escalations',
      open_recovery_ai_body:
        'RecoveryAI is handling everything else on its own right now.',
      open_all_title: "You're all caught up",
      open_all_body: 'Nothing needs a response right now.',
      resolved_title: 'No resolved feedback yet',
      resolved_body: 'Resolved feedback will show up here.',
      search_title: 'No matches',
      search_body: 'No feedback matches your search.',
      location_title: 'No feedback at these locations',
      location_body:
        'Try selecting a different location, or clear the location filter.',
    },
    error_title: "We couldn't load feedback",
    retry: 'Retry',
    view_more: 'View %{count} more',
    view_more_plural: 'View %{count} more',
  },
  status_line: {
    needs_response: 'Needs response',
    // Followed by the RecoveryAI wordmark badge (alt: recovery_ai_alt).
    escalated_by: 'Escalated by',
    resolved_by: 'Resolved by %{name}',
    resolved_by_prefix: 'Resolved by',
    recovery_ai_alt: 'RecoveryAI',
    positive: 'Positive · no reply needed',
  },
  thread: {
    order_title: 'Order #%{orderNumber}',
    order_title_fallback: 'Order',
    mark_resolved: 'Mark as resolved',
    unresolve: 'Unresolve',
    details_toggle: 'Details',
    details_toggle_aria: 'Show order and customer details',
    nothing_selected_title: 'Nothing selected',
    nothing_selected_body:
      'Select feedback from the list to open the conversation and respond.',
    loading_aria: 'Loading conversation',
    error_title: "We couldn't load this conversation",
    // For a failed header action (Mark as resolved / Unresolve) — distinct
    // from `error_title`, which is the detail QUERY's load failure. Shown
    // under the header even when the composer is visible; a failed Send
    // shows in the composer instead (`composer.error_banner*`). `_validation`
    // / `_permission` below are the 422 / 403 variants (AI-1350).
    action_error: "We couldn't complete that action. Please try again.",
    // S4 (AI-1350)
    action_error_validation:
      "We couldn't complete that action. This case may have already been updated by someone else.",
    action_error_permission:
      "You don't have permission to do that. Ask an admin for access.",
    retry: 'Retry',
    customer_heading: 'Customer: %{name}',
    // Curly quotes around the guest's opening rating comment only — matches
    // the prototype's data-layer quoting (VISUAL-SPEC.md §1 "Quote marks";
    // same repo convention as `list.preview_quoted` against raw Unicode/
    // HTML entities in JSX). Later guest chat turns render unquoted.
    quoted_comment: '“%{text}”',
    // Fallback for a null/missing customer name — distinct from
    // `list.unknown_guest` ("Guest") so this file's copy stays
    // self-contained per-namespace.
    unknown_guest_fallback: 'Guest',
    // `resolvedBy.kind === 'ai'` has no persisted name (docs/03-api-
    // contract.md) — this is the display name substituted in its place for
    // both the resolved marker and the reopened marker.
    recovery_ai_name: 'RecoveryAI',
    // Exact copy per docs/06-ux-rules.md "👍 row selected" (✓ closed).
    thumbs_up_note:
      "%{firstName} rated this order thumbs-up. Thumbs-up ratings don't collect a comment.",
    // Exact copy per docs/06-ux-rules.md "Comment-less 👎 thread" (✓ closed).
    no_comment_note:
      '%{firstName} rated the order thumbs-down but left no comment.',
    // FR-57 default ack text (client-side today, per docs/06-ux-rules.md
    // "Automated-ack text").
    automated_ack: 'Thanks for your feedback.',
    automated_ack_label: 'Automated message',
    // TODO(FR-57, Robert): enable a "Configure" link here once Settings ›
    // Guest experience ships (docs/06-ux-rules.md "Configure link").
    escalation_label: 'RecoveryAI escalation',
    // Fallback for a null `FeedbackInboxEscalation.reason`; a present reason
    // is a full backend sentence and renders verbatim.
    escalation_body_unknown: 'Escalated this issue',
    // "to <b>guest</b> · subject" — the guest name renders bold between these;
    // the subject part is dropped for a blank-subject reply (AI-1415).
    email_to: 'to',
    email_subject: '· %{subject}',
    sent_by_email: 'Sent by email',
    resolved_marker_label: 'Resolved by',
    reopened_marker: 'Reopened by %{name}',
    // Operator-side counterpart to `recovery_ai_name`. Load-bearing on the
    // happy path, not just for unpersisted names: the optimistic resolve
    // patch (api/feedbackInbox.ts `onQueryStarted`) sets `resolvedBy.name`
    // to null because the authoritative name isn't known until the refetch
    // lands, so every resolve renders this until then.
    resolved_by_fallback: 'a team member',
    // S4 (AI-1403)
    // Restrict audit note (v83 "Customer [Restricted]" block). `guest` is the
    // guest's name only with PII consent; `operator` starts the sentence, so
    // its fallback is capitalised (unlike `resolved_by_fallback`).
    restricted_note: {
      label: 'Customer',
      pill: 'Restricted',
      body: '%{operator} restricted %{guest} from RecoveryAI. Future thumbs-downs from this guest will skip AI resolution. This case was closed automatically.',
      guest_fallback: 'this customer',
      operator_fallback: 'A team member',
    },
  },
  composer: {
    title: 'Reply to guest as an email',
    reward_type_group_aria: 'Choose how to make this right',
    offer_radio: 'Offer',
    points_radio: 'Points',
    search_rewards_placeholder: 'Search rewards…',
    no_rewards_match: 'No rewards match your search.',
    rewards_loading: 'Loading rewards…',
    rewards_error: "We couldn't load your rewards.",
    points_input_aria: 'Points to grant',
    points_placeholder: 'e.g. 100',
    points_suffix: 'points',
    // Count-aware pair for prose (the reward-only prefill body), kept
    // SEPARATE from `points_suffix` above — that one is the static
    // endEnhancer beside the numeric field, where the call site has no
    // count and "points" is always right. react-redux-i18n picks the
    // `_plural` form off `count` natively (context/knowledge/
    // i18n-pluralization.md).
    points_amount: '%{count} point',
    points_amount_plural: '%{count} points',
    points_helper: "Added to the guest's loyalty balance. Whole numbers only.",
    points_error: 'Enter a whole number greater than 0.',
    subject_label: 'Subject',
    subject_default: 'Re: your order #%{orderNumber}',
    subject_default_fallback: 'Re: your recent feedback',
    message_label: 'Message',
    message_placeholder: 'Write a reply to %{firstName}…',
    // Reward-only default body (Q7, ✓ closed) — visible, editable prefill,
    // never sent silently.
    reward_only_body: "Here's %{offer} to make up for it…",
    // v70: single toggle button in the bottom action bar (VISUAL-SPEC.md
    // §3/§5) — collapsed label; `remove_reward` (below) doubles as the
    // expanded label.
    add_reward: 'Add reward',
    remove_reward: 'Remove reward',
    // S4 (AI-1354)
    remove_offer_aria: 'Remove %{offer}',
    send: 'Send email and resolve',
    discard_confirm_title: 'Discard your reply to %{name}?',
    discard_confirm_body:
      'This reply has not been sent. Switching feedback will lose it.',
    discard_confirm_cta: 'Discard',
    discard_cancel_cta: 'Keep editing',
    view_only_banner:
      'View-only access. You can read and filter feedback. Replying and resolving are handled by an admin.',
    view_only_tooltip: 'Requires "Feedback" permission (view and edit)',
    grant_permission_tooltip: 'Requires reward-issuance permission',
    cannot_email_notice:
      "This guest can't be emailed. You can still mark this as resolved.",
    error_banner: "We couldn't send that reply. Please try again.",
    // S4 (AI-1350)
    error_banner_validation:
      "We couldn't send that reply. This case may already be resolved, or the reward may no longer be available.",
    error_banner_permission:
      "You don't have permission to send this reply. Ask an admin for access.",
    no_reply_needed_title: 'No reply needed',
    no_reply_needed_body:
      "%{name} rated their order thumbs-up. Thumbs-up ratings don't collect a comment, so there's nothing to respond to.",
  },
  rail: {
    tabs: {
      customer: 'Customer',
      order_info: 'Order info',
      recent_feedback: 'Recent feedback',
    },
    tabs_aria: 'Customer and order details',
    loading_aria: 'Loading customer and order details',
    error_title: "We couldn't load customer and order details",
    retry: 'Retry',
    customer: {
      title: 'Customer',
      // No trailing arrow glyph — the link now carries a real
      // `components/Icons/ExternalLink` icon instead (VISUAL-SPEC.md
      // correction 2026-09-20, "no raw Unicode glyphs in JSX or strings").
      view_profile: 'View profile',
      // FR-67 "..." menu (RecoveryAI cases only).
      actions_menu_aria: 'More actions',
      restrict: 'Restrict this customer',
      restricted_pill: 'Restricted',
      restrict_no_permission: 'Requires "Merchant settings" permission',
      restrict_already_restricted: 'Customer is already restricted.',
      // Restrict resolves this case; the `_with_others` pair is used when the
      // modal's close-others box was checked (FR-67).
      restrict_success:
        'Customer restricted. This case was marked as resolved.',
      restrict_success_with_others:
        'Customer restricted. This case and their %{count} other open escalation were marked as resolved.',
      restrict_success_with_others_plural:
        'Customer restricted. This case and their %{count} other open escalations were marked as resolved.',
      // Split to match `components.customer_info`'s field-order parity
      // (AI-1332) — was a single combined `name` row.
      first_name: 'First name',
      last_name: 'Last name',
      loyalty_tier: 'Loyalty tier',
      total_visits: 'Total visits',
      lifetime_spend: 'Lifetime spend',
      email: 'Email',
      phone: 'Phone number',
      prior_resolution_history: 'Prior resolution history',
      // Not a pluralized sentence (no word changes shape with count), so no
      // key/key_plural pair is needed (same reasoning as kind_count_srlabel
      // above).
      prior_resolution_history_value: '%{count} resolved',
      pii_hidden: '—',
      // Distinct from `pii_hidden` above — this is a genuinely missing
      // aggregate (the Q15-pending FR-63 fields below, absent until BE ships
      // them), not a PII-consent gate. Same rendered value today, but the
      // two shouldn't share a name: a future PII-hidden vs. field-not-yet-
      // shipped distinction (e.g. different copy or an icon) would need to
      // change these independently.
      value_unavailable: '—',
      // FR-63 (docs/06-ux-rules.md "Look-back rail (FR-63)") — replace
      // Email/Phone/Prior-resolution-history on a look-back target.
      // TODO(Q15): field naming/definition pending BE confirmation.
      thumbs_down_90d: 'Thumbs-down · 90d',
      thumbs_down_90d_value: '%{count} · %{amount} rewarded',
      returned_after: 'Returned after',
      returned_after_value: '%{returned} of %{total}',
    },
    order_info: {
      title: 'Order info',
      date_time: 'Date & time',
      ordering_location: 'Ordering location',
      delivery_method: 'Delivery method',
      unavailable: 'Order details unavailable',
      // Distinct from `unavailable` above (the whole-panel empty state) —
      // this is a single missing value within an otherwise-rendered Date/
      // Location field (the item/total breakdown's own placeholder lives in
      // `components.order_snapshot_receipt.value_unavailable` now that
      // rendering moved to the shared `OrderSnapshotReceipt` component).
      line_unavailable: '—',
    },
    recent_feedback: {
      title: 'Recent feedback',
      resolved_by: 'resolved by %{name}',
      // `resolvedBy.kind === 'ai'` carries no persisted name (mirrors
      // `thread.recovery_ai_name`) — kept self-contained per this file's
      // per-namespace convention rather than reaching into `thread.*`.
      recovery_ai_name: 'RecoveryAI',
      empty: 'No previous feedback from this guest.',
      no_comment: 'Rated order but left no comment',
      view_more: 'View %{count} more',
      view_more_plural: 'View %{count} more',
    },
  },
  lookback: {
    back_to_feedback: 'Back to feedback',
    back_to_feedback_aria: 'Return to the feedback list',
    banner:
      'You’re looking at a <strong class="grey-90">past order</strong> from <strong class="grey-90">%{date}</strong> for this guest – return to the inbox anytime.',
    heading: 'Past feedback · %{date}',
    opened_announcement: 'Viewing past feedback',
    closed_announcement: 'Returned to the feedback list',
  },
  off_states: {
    written_feedback_off_title: 'Written feedback is off',
    written_feedback_off_body:
      "Guests are still rating orders, you'll find those in Order Ratings.",
    turn_on_written_feedback: 'Turn on written feedback',
    view_order_ratings: 'View order ratings ↗',
    comments_off_title: "Comments are off – you're seeing ratings only.",
    comments_off_body:
      'Turn on written feedback to collect comments and reply.',
  },
  actions: {
    resolved_announcement: 'Resolved — showing %{name}',
    unresolved_announcement: 'Unresolved',
    // S4 (AI-1351)
    resolved_lookback_announcement: 'Resolved',
  },
  layout: {
    filter_bar_landmark: 'Feedback filters',
    list_landmark: 'Feedback list',
    thread_landmark: 'Conversation',
    rail_landmark: 'Customer and order details',
    narrow_viewport_notice:
      'Your screen is too narrow to show the feedback inbox. Try a wider window.',
    // 900-1199px collapses the rail behind this drawer (docs/06-ux-rules.md
    // "Responsive floor" / P10 D3).
    rail_drawer_aria: 'Customer and order details',
  },
}
